import { useCallback, useEffect, useRef, useState, Fragment } from "react";
import downArrow from "../../assets/svg/DownArrow.svg";
import SortingICon from "../../assets/svg/SortingICon.svg";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import SubtaskArrow from "@/assets/svg/SubtaskArrow.svg";
import Spinner from "../ui/spinner";

export type ColumeDef = {
  className?: (data: any) => string;
  key: string;
  header: string;
  sorting?: boolean;
  width?: number;
  hidden?: boolean;
  onCellRender?: (data: any) => JSX.Element;
  onHeaderRender?: (header: string) => JSX.Element;
  colspan?: (data: any) => number;
};
export type Props = {
  columnDef?: ColumeDef[];
  setColumnDef?: (cols: ColumeDef[]) => void;
  hiddenColumnsKey?: string;
  data: any[];
  className?: string;
  tableClassName?: string;
  hidePagination?: boolean;
  hideHeaders?: boolean;
  onAccordionRender?: (data: any) => JSX.Element;
  expandAll?: boolean;
  onRowClassName?: (data: any) => string;
  showDropDown?: (data: any) => string;
  showDropDownArrow?: (data: any) => string;
  showDropDownImg?: (data: any) => string;
  hederClassName?: string;
  noDataFound?: (data: any) => JSX.Element;
  showBody?: boolean;
  notShowNoDataFound?: boolean;
  dataToShow?: (data: any) => void;
  loading?: boolean;
  enableColumnCustomization?: boolean;
  /** Applied to the <td> that wraps accordion / nested table content (default removes padding so nested grids align). */
  accordionRowTdClassName?: string;
  hideScrollbar?: boolean;
};

const pageOptions = [
  { label: 10, value: 10 },
  { label: 25, value: 25 },
  { label: 50, value: 50 },
  { label: 100, value: 100 },
];

// ─── Resizable / Draggable / Right-Click Header ──────────────────────────────
function TableHeader({
  columns,
  setColumns,
  hiddenColumnsKey,
  sortOn,
  ascendingToggle,
  onSort,
  hideHeaders,
  hederClassName,
  enableColumnCustomization = false
}: {
  columns: ColumeDef[];
  setColumns: (cols: ColumeDef[]) => void;
  hiddenColumnsKey: string;
  sortOn: string | undefined;
  ascendingToggle: boolean;
  onSort: (key: string) => void;
  hideHeaders?: boolean;
  hederClassName?: string;
  enableColumnCustomization?: boolean;
}) {
  const { t } = useTranslation();

  // ── Resize ────────────────────────────────────────────────────────────────
  const [isResizing, setIsResizing] = useState(false);
  const [resizingIdx, setResizingIdx] = useState<number | null>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);

  const visibleColumns = columns.filter((c) => !c.hidden);

  const handleResizeMouseDown = (e: React.MouseEvent, colKey: string) => {
    if (!enableColumnCustomization) return;
    e.preventDefault();
    const idx = visibleColumns.findIndex((c) => c.key === colKey);
    setIsResizing(true);
    setResizingIdx(idx);
    startXRef.current = e.clientX;
    startWidthRef.current = visibleColumns[idx].width ?? 120;
  };

  useEffect(() => {
    if (!isResizing || resizingIdx === null) return;
    const onMove = (e: MouseEvent) => {
      const diff = e.clientX - startXRef.current;
      const newWidth = Math.max(startWidthRef.current + diff, 60);
      const key = visibleColumns[resizingIdx].key;
      setColumns(
        columns.map((c) => (c.key === key ? { ...c, width: newWidth } : c))
      );
    };
    const onUp = () => {
      setIsResizing(false);
      setResizingIdx(null);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [isResizing, resizingIdx, columns]);

  // ── Drag-to-reorder ───────────────────────────────────────────────────────
  const dragColKey = useRef<string | null>(null);
  const [dropTargetKey, setDropTargetKey] = useState<string | null>(null);

  const handleDragStart = (key: string) => {
    if (!enableColumnCustomization) return;
    if (!isResizing) dragColKey.current = key;
  };
  const handleDragOver = (e: React.DragEvent, key: string) => {
    if (!enableColumnCustomization) return;
    e.preventDefault();
    if (!isResizing && dragColKey.current) setDropTargetKey(key);
  };
  const handleDrop = (dropKey: string) => {
    if (!enableColumnCustomization) return;
    if (isResizing || !dragColKey.current || dragColKey.current === dropKey) return;
    const reordered = [...columns];
    const fromIdx = reordered.findIndex((c) => c.key === dragColKey.current);
    const toIdx = reordered.findIndex((c) => c.key === dropKey);
    const [dragged] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, dragged);
    setColumns(reordered);
    dragColKey.current = null;
    setDropTargetKey(null);
  };
  const handleDragEnd = () => {
    dragColKey.current = null;
    setDropTargetKey(null);
  };

  // ── Right-click context menu ──────────────────────────────────────────────
  type MenuPos = { x: number; y: number; colKey: string };
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPos>({ x: 0, y: 0, colKey: "" });
  const menuRef = useRef<HTMLDivElement>(null);

  // ── Unhide columns menu ───────────────────────────────────────────────────
  const hiddenColumns = columns.filter((c) => c.hidden);
  const [addMenuVisible, setAddMenuVisible] = useState(false);
  const addMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hideMenu = () => setMenuVisible(false);
    const hideAddMenu = () => setAddMenuVisible(false);

    const hideAll = () => { hideMenu(); hideAddMenu(); };

    document.addEventListener("click", hideAll);
    window.addEventListener("scroll", hideAll);
    window.addEventListener("resize", hideAll);
    return () => {
      document.removeEventListener("click", hideAll);
      window.removeEventListener("scroll", hideAll);
      window.removeEventListener("resize", hideAll);
    };
  }, []);

  const hideColumn = (colKey: string) => {
    const stored: string[] = JSON.parse(
      localStorage.getItem(hiddenColumnsKey) ?? "[]"
    );
    localStorage.setItem(
      hiddenColumnsKey,
      JSON.stringify([...stored, colKey])
    );
    setColumns(columns.map((c) => (c.key === colKey ? { ...c, hidden: true } : c)));
    setMenuVisible(false);
  };

  const unhideColumn = (colKey: string) => {
    const stored: string[] = JSON.parse(
      localStorage.getItem(hiddenColumnsKey) ?? "[]"
    );
    localStorage.setItem(
      hiddenColumnsKey,
      JSON.stringify(stored.filter((k) => k !== colKey))
    );
    setColumns(columns.map((c) => (c.key === colKey ? { ...c, hidden: false } : c)));
    setAddMenuVisible(false);
  };

  const handleContextMenu = (e: React.MouseEvent, colKey: string) => {
    if (!enableColumnCustomization) return;
    e.preventDefault();
    const menuW = 200;
    const menuH = 120;
    let x = e.clientX;
    let y = e.clientY;
    if (x + menuW > window.innerWidth) x = window.innerWidth - menuW - 5;
    if (y + menuH > window.innerHeight) y = window.innerHeight - menuH - 5;
    setMenuPos({ x, y, colKey });
    setMenuVisible(true);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <tr
      className={`text-left border-b border-[#D1D1D1aa] sticky top-0 bg-[#e6e6e6] !tracking-wide !font-normal p-2 ${hederClassName ?? ""} ${hideHeaders
        ? "!bg-transparent !p-0 !h-0 border-none !text-transparent"
        : ""
        }`}
    >
      {/* spacer cells — widths match body row (arrow / expand columns) */}
      <th className="first:rounded-tl-md last:rounded-tr-md w-[14px] min-w-[14px] max-w-[14px] p-0 box-border" />
      <th className="first:rounded-tl-md last:rounded-tr-md w-10 min-w-10 max-w-10 p-0 box-border" />

      {visibleColumns.map((col) => (
        <th
          key={col.key}
          data-column={col.key}
          className={`py-2.5 px-3 sticky top-0 font-normal text-center select-none first:rounded-tl-md last:rounded-tr-md ${hideHeaders ? "!bg-transparent !py-0 !h-0 absolute" : ""
            } ${dropTargetKey === col.key ? "opacity-50 bg-slate-300" : ""}`}
          style={{ minWidth: col.width ? `${col.width}px` : undefined }}
          draggable={!isResizing}
          onDragStart={() => handleDragStart(col.key)}
          onDragEnd={handleDragEnd}
          onDragOver={(e) => handleDragOver(e, col.key)}
          onDrop={() => handleDrop(col.key)}
          onContextMenu={(e) => handleContextMenu(e, col.key)}
        >
          {!hideHeaders && col.sorting ? (
            <div
              className="flex text-nowrap gap-1 items-center justify-center cursor-pointer group"
              onClick={() => onSort(col.key)}
            >
              {col.onHeaderRender && col.header
                ? col.onHeaderRender(col.header)
                : col.header}
              <img
                src={SortingICon}
                className={`${sortOn === col.key && ascendingToggle ? "rotate-180" : ""} ${sortOn === col.key ? "invert" : "invert opacity-25"
                  }`}
              />
            </div>
          ) : col.onHeaderRender && col.header ? (
            col.onHeaderRender(col.header)
          ) : (
            col.header
          )}

          {/* Resize handle */}
          {enableColumnCustomization && (
            <div
              className="absolute right-0 top-1/4 h-1/2 w-1 cursor-col-resize bg-gray-300 hover:bg-gray-500 rounded"
              onMouseDown={(e) => handleResizeMouseDown(e, col.key)}
            />
          )}

          {/* Context menu */}
          {menuVisible && menuPos.colKey === col.key && (
            <div
              ref={menuRef}
              style={{
                position: "fixed",
                top: menuPos.y,
                left: menuPos.x,
                background: "#fff",
                border: "1px solid #ccc",
                boxShadow: "2px 2px 6px rgba(0,0,0,0.2)",
                padding: "8px",
                zIndex: 9999,
                width: "200px",
              }}
              className="rounded-md flex flex-col gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="px-2 py-1 text-gray-600 hover:bg-slate-100 rounded cursor-pointer text-sm"
                onClick={() => hideColumn(col.key)}
              >
                {t("Hide column")}
              </div>
              <div
                className="px-2 py-1 text-gray-600 hover:bg-slate-100 rounded cursor-pointer text-sm"
                onClick={() => { onSort(col.key); setMenuVisible(false); }}
              >
                {t("Sort ascending")}
              </div>
              <div
                className="px-2 py-1 text-gray-600 hover:bg-slate-100 rounded cursor-pointer text-sm"
                onClick={() => { onSort(col.key); setMenuVisible(false); }}
              >
                {t("Sort descending")}
              </div>
            </div>
          )}
        </th>
      ))}

      {/* Add hidden column button */}
      {enableColumnCustomization && hiddenColumns.length > 0 && !hideHeaders && (
        <th className="py-2.5 px-3 sticky top-0 text-center first:rounded-tl-md last:rounded-tr-md w-12 min-w-12">
          <div className="relative flex justify-center items-center h-full">
            <button
              className="text-gray-500 hover:text-black hover:bg-gray-200 rounded-md px-2 py-1 flex items-center justify-center font-bold text-lg"
              onClick={(e) => { e.stopPropagation(); setAddMenuVisible(!addMenuVisible); }}
              title={t("Show hidden columns")}
            >
              +
            </button>
            {addMenuVisible && (
              <div
                ref={addMenuRef}
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  background: "#fff",
                  border: "1px solid #ccc",
                  boxShadow: "2px 2px 6px rgba(0,0,0,0.2)",
                  padding: "8px",
                  zIndex: 9999,
                  minWidth: "150px",
                }}
                className="rounded-md flex flex-col gap-1 text-left font-normal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-xs text-gray-400 font-semibold mb-1 px-2 uppercase">{t("Hidden")}</div>
                {hiddenColumns.map((c) => (
                  <div
                    key={c.key}
                    className="px-2 py-1 text-gray-600 hover:bg-slate-100 rounded cursor-pointer text-sm truncate"
                    onClick={() => unhideColumn(c.key)}
                  >
                    {c.header}
                  </div>
                ))}
              </div>
            )}
          </div>
        </th>
      )}
    </tr>
  );
}

// ─── Main Table Component ─────────────────────────────────────────────────────
function Table(props: Props) {
  const { t } = useTranslation();
  const { data, className, showBody = true } = props;
  const [dataSource, setDataSource] = useState(data);

  const [dataPerPage, setDataPerPage] = useState(pageOptions[0].value);
  const [pages, setPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [ascendingToggle, setAscendingToggle] = useState<boolean>(false);
  const [accordions, setAccordionshow] = useState<number[]>([]);
  const prevExpandAllRef = useRef<boolean | undefined>(undefined);
  const table = useRef<HTMLDivElement>(null);
  const tableRow = useRef<HTMLTableRowElement>(null);

  // ── Column state (internal if parent doesn't manage it) ──────────────────
  const hiddenKey = props.hiddenColumnsKey ?? "table_hidden_columns";

  const [internalColumns, setInternalColumns] = useState<ColumeDef[]>(() => {
    const hidden: string[] = JSON.parse(
      localStorage.getItem(hiddenKey) ?? "[]"
    );
    return (props.columnDef ?? []).map((c) => ({
      ...c,
      hidden: hidden.includes(c.key) || c.hidden,
    }));
  });

  // Sync when parent passes new columnDef (e.g., after data loads or state changes)
  // We merge the new column definitions (for updated renderers) with our existing order and widths.
  useEffect(() => {
    const hidden: string[] = JSON.parse(
      localStorage.getItem(hiddenKey) ?? "[]"
    );
    setInternalColumns((prev) => {
      const incomingCols = props.columnDef ?? [];

      // Preserve the current order of keys
      const newInternal = prev
        .map((existingCol) => {
          const matchingIncoming = incomingCols.find((c) => c.key === existingCol.key);
          if (!matchingIncoming) return null; // column removed
          return {
            ...matchingIncoming,
            width: existingCol.width ?? matchingIncoming.width,
            hidden: hidden.includes(matchingIncoming.key) || matchingIncoming.hidden,
          };
        })
        .filter(Boolean) as ColumeDef[];

      // Add any brand new columns to the end
      incomingCols.forEach((incoming) => {
        if (!newInternal.find((c) => c.key === incoming.key)) {
          newInternal.push({
            ...incoming,
            hidden: hidden.includes(incoming.key) || incoming.hidden,
          });
        }
      });

      return newInternal;
    });
  }, [props.columnDef, hiddenKey]);

  const handleSetColumns = (cols: ColumeDef[]) => {
    setInternalColumns(cols);
    props.setColumnDef?.(cols);
  };

  const visibleColumns = internalColumns.filter((c) => !c.hidden);

  useEffect(() => {
    let count = 1;
    if (!props.hidePagination) {
      setDataSource(data.slice(0, dataPerPage));
      count = Math.ceil(data.length / dataPerPage);
      setPages(count);
    } else {
      setDataSource(data);
    }
    if (count > 1) {
      const [startIndex, endIndex] = calculateIndices(currentPage);
      changePage(currentPage, startIndex, endIndex);
    } else {
      setCurrentPage(1);
    }
  }, [data, dataPerPage, props.hidePagination, props]);

  const nextPage = () => {
    if (currentPage < pages) {
      const newCurrentPage = currentPage + 1;
      const [startIndex, endIndex] = calculateIndices(newCurrentPage);
      changePage(newCurrentPage, startIndex, endIndex);
    }
  };
  const previousPage = () => {
    if (currentPage >= 1) {
      const newCurrentPage = currentPage - 1;
      const [startIndex, endIndex] = calculateIndices(newCurrentPage);
      changePage(newCurrentPage, startIndex, endIndex);
    }
  };

  const reactSelectStyle = {
    control: (
      provided: Record<string, unknown>,
      state: { isFocused: boolean },
    ) => ({
      ...provided,
      border: "1px solid #E7E7E7",
      paddingTop: "0rem",
      paddingBottom: "0rem",
      outline: state.isFocused ? "2px solid #535EF9" : "0px solid #E7E7E7",
      boxShadow: state.isFocused ? "0px 0px 0px #535EF9" : "none",
      "&:hover": {
        outline: state.isFocused ? "1px solid #535EF9" : "1px solid #E7E7E7",
        boxShadow: "0px 0px 0px #535EF9",
      },
    }),
    option: (
      provided: Record<string, unknown>,
      state: { isSelected: boolean },
    ) => ({
      ...provided,
      color: state.isSelected ? "white" : "black",
      background: state.isSelected ? "#535EF9" : "white",
      "&:hover": {
        color: "white",
        background: "#535EF999",
      },
    }),
  };

  const calculateIndices = useCallback(
    (page: number) => {
      const startIndex = (page - 1) * dataPerPage;
      const endIndex = startIndex + dataPerPage;
      return [startIndex, endIndex];
    },
    [dataPerPage],
  );

  const changePage = useCallback(
    (newPage: number, startIndex: number, endIndex: number) => {
      setCurrentPage(newPage);
      setDataSource(data.slice(startIndex, endIndex));
    },
    [data],
  );

  function dynamicSort(property: any) {
    return function (a: string, b: string) {
      let aValue, bValue;

      aValue = a[property];
      bValue = b[property];
      if (
        typeof aValue === "string" &&
        !isValidDate(aValue) &&
        !isValidDate(bValue)
      ) {
        aValue = aValue?.toUpperCase();
        bValue = bValue?.toUpperCase();
      }

      if (isValidDate(aValue) && isValidDate(bValue)) {
        const [aDay, aMonth, aYear] = parseDate(aValue);
        const [bDay, bMonth, bYear] = parseDate(bValue);

        aValue = new Date(`${aYear}-${aMonth}-${aDay}`);
        bValue = new Date(`${bYear}-${bMonth}-${bDay}`);
      }
      if (typeof aValue === "string" && !isNaN(Number(aValue))) {
        aValue = Number(aValue);
      }

      if (typeof bValue === "string" && !isNaN(Number(bValue))) {
        bValue = Number(bValue);
      }
      let comparison = 0;

      if (aValue > bValue) {
        comparison = 1;
      } else if (aValue < bValue) {
        comparison = -1;
      }

      return !ascendingToggle ? comparison * -1 : comparison;
    };
  }
  function toggle(index: number) {
    if (accordions && accordions?.includes(index)) {
      setAccordionshow(accordions.filter((num) => num !== index));
    } else {
      setAccordionshow([...(accordions ?? []), index]);
    }
  }

  function isValidDate(dateString: string) {
    const regex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    return regex.test(dateString);
  }
  function parseDate(dateString: string) {
    const [day, month, year] = dateString.split("/").map(Number);
    return [day, month, year];
  }
  const [sortOn, setsortOn] = useState<string>();
  const sorting = (key: string) => {
    setDataSource(data.sort(dynamicSort(key)));
    if (props.dataToShow) {
      setAccordionshow([]);
      props.dataToShow(data.sort(dynamicSort(key)));
    }
    setsortOn(key);
    setCurrentPage(1);
    setAscendingToggle((prev) => !prev);
  };

  useEffect(() => {
    if (props.expandAll) {
      const expandedIndices: number[] = [];
      dataSource.forEach((_item, index) => {
        if (
          dataSource.filter(
            (item) =>
              props.onAccordionRender &&
              props.onAccordionRender(item).props.children,
          ).length > 0
        ) {
          expandedIndices.push(index);
        }
      });
      setAccordionshow(expandedIndices);
    } else if (prevExpandAllRef.current === true) {
      setAccordionshow([]);
    }
    prevExpandAllRef.current = props.expandAll;
  }, [props.expandAll, dataSource]);

  const goToPage = useCallback(
    (page: number) => {
      if (page < 1 || page > pages) return;
      const [start, end] = calculateIndices(page);
      changePage(page, start, end);
    },
    [pages, calculateIndices, changePage],
  );
  return (
    <>
      <div
        ref={table}
        className={`flex flex-col justify-between border-l border-r !shadow rounded-md text-sm h-full w-full ${className ?? ""
          }`}
      >
        <div className={`overflow-auto !rounded-md ${props.hideScrollbar ? "hide-scrollbar" : "thin-scrollbar"}`}>
          <table className={`w-full ${props.tableClassName ?? ""}`}>
            <thead
              className={`${props.hederClassName}  bg-[#FAFAFB]  ${props.hideHeaders ? "collapse" : ""
                }`}
            >
              {!props.hideHeaders ? (
                <TableHeader
                  columns={internalColumns}
                  setColumns={handleSetColumns}
                  hiddenColumnsKey={hiddenKey}
                  sortOn={sortOn}
                  ascendingToggle={ascendingToggle}
                  onSort={sorting}
                  hideHeaders={props.hideHeaders}
                  hederClassName={props.hederClassName}
                  enableColumnCustomization={props.enableColumnCustomization}
                />
              ) : (
                /* hidden headers — in-flow zero-height row so columns get min widths (absolute th breaks nested tables). */
                <tr className="h-0 overflow-hidden border-0 !bg-transparent" aria-hidden>
                  <th className="!p-0 !h-0 !border-0 w-[14px] min-w-[14px] max-w-[14px]" />
                  <th className="!p-0 !h-0 !border-0 w-10 min-w-10 max-w-10" />
                  {visibleColumns.map((col) => (
                    <th
                      key={col.key}
                      className="!p-0 !h-0 !border-0 !leading-none overflow-hidden opacity-0 pointer-events-none"
                      style={{
                        minWidth: col.width ? `${col.width}px` : undefined,
                      }}
                    />
                  ))}
                  {internalColumns.some((c) => c.hidden) && (
                    <th className="!p-0 !h-0 !border-0 w-12 min-w-12" />
                  )}
                </tr>
              )}
            </thead>
            {showBody && (
              <tbody>
                {dataSource.map((item, index) => {
                  return (
                    <Fragment key={index}>
                      <tr
                        ref={tableRow}
                        className={`border-b border-gray-450/30 even:bg-[#FAFAFB] ${props.onRowClassName?.(item) || ""
                          }`}
                      >
                        <td className="w-[14px] min-w-[14px] max-w-[14px] p-0 box-border">
                          {
                            <div className="img w-3.5 h-3.5 hidden">
                              <img src={downArrow} className={`hidden`} />
                            </div>
                          }
                        </td>
                        {props.onAccordionRender &&
                          dataSource.filter(
                            (item) =>
                              props.onAccordionRender &&
                              props.onAccordionRender(item).props.children,
                          ).length > 0 ? (
                          <td
                            onClick={() => toggle(index)}
                            className={`cursor-pointer w-10 min-w-10 max-w-10 p-0 box-border ${props.showDropDownArrow?.(
                              item,
                            )}`}
                          >
                            <div className="flex items-center w-fit">
                              {props.onAccordionRender(item).props.children && (
                                <img
                                  src={downArrow}
                                  className={` w-3.5 min-w-3.5 ${accordions.includes(index) && "rotate-180 "
                                    }`}
                                />
                              )}
                              {
                                <img
                                  src={SubtaskArrow}
                                  className={
                                    "h-5 w-5 min-w-5 hidden " +
                                    props.showDropDownImg?.(item)
                                  }
                                />
                              }
                            </div>
                          </td>
                        ) : (
                          <td className="cursor-pointer w-10 min-w-10 max-w-10 p-0 box-border">
                            {
                              <div className="img w-3.5 h-3.5">
                                <img src={downArrow} className={`hidden`} />
                              </div>
                            }
                            {
                              <img
                                src={SubtaskArrow}
                                className={
                                  "h-5 w-5 min-w-5 hidden " +
                                  props.showDropDownImg?.(item)
                                }
                              />
                            }
                          </td>
                        )}
                        {visibleColumns.map((col: ColumeDef, colIdx: number) => {
                          return (
                            <td
                              key={col.key || colIdx}
                              className={`py-2 px-3 max-md:truncate lg:break-words ${col.className ? col.className(item) : ""
                                }`}
                              style={col.width ? { width: `${col.width}px`, minWidth: `${col.width}px` } : {}}
                              colSpan={
                                col.colspan
                                  ? col.colspan({
                                    index: colIdx,
                                    ...item,
                                  })
                                  : 1
                              }
                            >
                              <div
                                className={`flex justify-center items-center`}
                                style={col.width ? { width: `${col.width}px`, minWidth: `${col.width}px` } : {}}
                              >
                                {col.onCellRender && col.key
                                  ? col.onCellRender({
                                    index: colIdx,
                                    ...item,
                                  })
                                  : item[col.key]}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                      {accordions?.includes(index) && (
                        <tr
                          className={`transition-all duration-300 ease-in-out transform  ${accordions?.includes(index)
                            ? "opacity-100 scale-100"
                            : "opacity-0 scale-95"
                            }`}
                        >
                          <td
                            className={
                              props.accordionRowTdClassName ??
                              "p-0 align-top border-0"
                            }
                            colSpan={
                              visibleColumns?.length
                                ? visibleColumns?.length + 2 + (internalColumns.some(c => c.hidden) ? 1 : 0)
                                : visibleColumns?.length
                            }
                          >
                            {props.onAccordionRender &&
                              props.onAccordionRender(item)}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>
        {dataSource.length === 0 &&
          (props.loading ? (
            <Spinner className="animate-spin mx-auto my-10 scale-125" color="#535EF9" height={"30"} width={"30"} />
          ) : !props.notShowNoDataFound ? (
            <div className="w-full text-center  text-gray-400  my-10">
              {typeof props.noDataFound === "function"
                ? props.noDataFound(data)
                : t("no_data_found!")}
            </div>
          ) : null)}
        {!props.hidePagination && (
          <div className="flex items-center px-3 mb-2 justify-between mt-3">
            {/* Left: Per page */}
            <div className="flex items-center gap-2">
              <Select
                className="w-24  text-sm text-[#333333] font-semibold"
                defaultValue={pageOptions.find(
                  (page) => page.value === dataPerPage,
                )}
                options={pageOptions}
                menuPlacement="top"
                styles={reactSelectStyle}
                onChange={(e) => setDataPerPage(Number(e?.value))}
              />
              <span className="text-sm text-[#333333] font-semibold">
                per page
              </span>
            </div>

            <div className="flex items-center gap-1">
              {/* First */}
              <PaginationButton
                type="double-prev"
                disabled={currentPage === 1}
                onClick={() => goToPage(1)}
              />

              {/* Previous */}
              <PaginationButton
                type="prev"
                disabled={currentPage === 1}
                onClick={previousPage}
              />

              {/* Page Numbers */}
              {[...Array(pages)].slice(0, 3).map((_, i) => {
                const page = i + 1;
                return (
                  <PageNumber
                    key={page}
                    page={page}
                    active={page === currentPage}
                    onClick={() => goToPage(page)}
                  />
                );
              })}

              {pages > 3 && (
                <>
                  <span className="px-2 text-gray-400">…</span>
                  <PageNumber
                    page={pages}
                    active={currentPage === pages}
                    onClick={() => goToPage(pages)}
                  />
                </>
              )}

              {/* Next */}
              <PaginationButton
                type="next"
                disabled={currentPage === pages}
                onClick={nextPage}
              />

              {/* Last */}
              <PaginationButton
                type="double-next"
                disabled={currentPage === pages}
                onClick={() => goToPage(pages)}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Table;
const PaginationButton = ({
  disabled,
  onClick,
  type,
}: {
  disabled: boolean;
  onClick: () => void;
  type: "prev" | "next" | "double-prev" | "double-next";
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-200 
                 disabled:opacity-40 hover:bg-gray-100 transition"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        {type === "prev" && <path d="M15 18l-6-6 6-6" />}
        {type === "next" && <path d="M9 6l6 6-6 6" />}
        {type === "double-prev" && (
          <>
            <path d="M19 18l-6-6 6-6" />
            <path d="M11 18l-6-6 6-6" />
          </>
        )}
        {type === "double-next" && (
          <>
            <path d="M5 6l6 6-6 6" />
            <path d="M13 6l6 6-6 6" />
          </>
        )}
      </svg>
    </button>
  );
};

const PageNumber = ({
  page,
  active,
  onClick,
}: {
  page: number;
  active: boolean;
  onClick: () => void;
}) => {
  return (
    <button
      onClick={onClick}
      className={`w-8 h-8 rounded-md text-sm font-semibold transition
        ${active ? "bg-secondary text-white" : "text-gray-600 hover:bg-gray-100"
        }`}
    >
      {page}
    </button>
  );
};
